/**
 * StewardClaimModal - Explains steward role before user confirms a claim.
 * Shows capabilities and the proof-of-presence requirement.
 * @atomic @idempotent
 */
import { memo } from 'react'
import {
  XMarkIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'

const StewardClaimModal = memo(function StewardClaimModal({ entityName, onConfirm, onClose }) {
  const { t } = useTranslation()

  return (
    <div className="fixed inset-0 bg-black/50 z-[800] flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <div className="flex items-center gap-2">
            <ShieldCheckIcon className="h-5 w-5 text-primary" />
            <h2 className="text-base font-semibold text-gray-900">
              {t('vendor.claim.modal.title', 'Become a Steward')}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            onTouchEnd={onClose}
            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500 pointer-events-none" />
          </button>
        </div>

        {entityName && (
          <p className="px-4 pb-1 text-xs text-gray-500">{entityName}</p>
        )}

        <div className="px-4 pb-4">
          <p className="text-sm text-gray-600 mb-3">
            {t('vendor.claim.modal.whatYouCanDo', 'As a steward you can:')}
          </p>

          <ul className="space-y-2 mb-4">
            <li className="flex items-start gap-2 text-sm text-gray-700">
              <CheckCircleIcon className="h-4 w-4 text-primary flex-shrink-0 mt-0.5 pointer-events-none" />
              {t('vendor.claim.modal.bullet1', "Check in when you're open so followers know your location")}
            </li>
            <li className="flex items-start gap-2 text-sm text-gray-700">
              <CheckCircleIcon className="h-4 w-4 text-primary flex-shrink-0 mt-0.5 pointer-events-none" />
              {t('vendor.claim.modal.bullet2', 'Manage your menu and set your weekly schedule')}
            </li>
            <li className="flex items-start gap-2 text-sm text-gray-700">
              <CheckCircleIcon className="h-4 w-4 text-primary flex-shrink-0 mt-0.5 pointer-events-none" />
              {t('vendor.claim.modal.bullet3', 'Broadcast updates directly to your followers')}
            </li>
          </ul>

          {/* Proof-of-presence warning */}
          <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 rounded-lg border border-amber-200 mb-4">
            <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5 pointer-events-none" />
            <p className="text-xs text-amber-700">
              {t('vendor.claim.modal.popWarning', 'You must have spotted this entity at least once to claim stewardship.')}
            </p>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              onTouchEnd={onClose}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              {t('vendor.claim.modal.cancel', 'Cancel')}
            </button>
            <button
              type="button"
              onClick={() => { onClose(); onConfirm() }}
              onTouchEnd={(e) => { e.preventDefault(); onClose(); onConfirm() }}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-primary text-white hover:bg-primary/90 transition-colors"
            >
              {t('vendor.claim.modal.confirm', 'Become a steward')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
})

export default StewardClaimModal
